import {
  MutableRefObject, RefObject, useEffect, useRef,
} from 'react';

/**
 * Returns `true` if the browser supports `IntersectionObserver`
 */
const browserHasIntersectionObserver = () => typeof window !== 'undefined'
  && ('IntersectionObserver' in window)
  && ('IntersectionObserverEntry' in window)
  && ('intersectionRatio' in window.IntersectionObserverEntry.prototype);

const noop = () => { };

type UseIntersectionObserverOptions<T extends HTMLElement> = {
  /**
   * A reference to the node that should be observed
   */
  ref: MutableRefObject<T> | RefObject<T>;
  /**
   * A callback that will be triggered when the node intersects
   */
  callback: IntersectionObserverCallback;
  /**
   * The element that is used as the viewport for checking visibility of the target.
   * Must be the ancestor of the target. Defaults to the browser viewport if not
   * specified or if `null`.
   */
  root?: Element | null;
  /**
   * Margin around the root. Can have values similar to the CSS margin property,
   * e.g. "10px 20px 30px 40px" (top, right, bottom, left).
   * The values can be percentages. This set of values serves to grow or shrink
   * each side of the root element's bounding box before computing intersections.
   * Defaults to all zeros.
   */
  rootMargin?: string;
  /**
   * Either a single number or an array of numbers which indicate at what percentage
   * of the target's visibility the observer's callback should be executed. If you
   * only want to detect when visibility passes the 50% mark, you can use a value of
   * 0.5. If you want the callback to run every time visibility passes another 25%,
   * you would specify the array [0, 0.25, 0.5, 0.75, 1]. Passing 0 (meaning as soon
   * as even one pixel is visible, the callback will be run). A value of 1.0 means
   * that the threshold isn't considered passed until every pixel is visible.
   */
  threshold?: number | number[];
};

/**
 * Registers a callback with the intersection observer that will be called
 * when the reference element enters or exits the specified margin.
 * If the browser does not support `IntersectionObserver`, the callback will never be called.
 */
const useIntersectionObserver = <T extends HTMLElement>({
  ref,
  callback,
  root,
  rootMargin,
  threshold,
}: UseIntersectionObserverOptions<T>): void => {
  const observer = useRef<IntersectionObserver>();

  useEffect(() => {
    if (!browserHasIntersectionObserver()) {
      return noop;
    }

    const node = ref.current;
    if (!node) {
      return noop;
    }

    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver(callback, {
      threshold,
      root,
      rootMargin,
    });

    const { current: currentObserver } = observer;
    currentObserver.observe(node);

    return () => {
      currentObserver.disconnect();
    };
  }, [ref, root, rootMargin, threshold, callback]);
};

export default useIntersectionObserver;
